/**
 * USE-WORK-ORDER-TASK-SELECTION.JS - Work Order Task Selection Management Hook
 * 
 * SUMMARY:
 * Custom hook that manages complex task and item selection logic for work orders.
 * Handles task expansion to subcontractor items, attribution management, and line item operations.
 */

import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { TaskType } from '../../../../util/enums';
import { errorSnackBar } from '../../../../actions/common.action';
import {
  buildDescription,
  normaliseSubcontractorItem
} from '../utils/work-order-helpers';

/**
 * Custom hook for managing work order task and item selection.
 * 
 * @param {Object} params - Hook parameters
 * @param {Map} params.taskByIdRef - Reference to taskById map (taskId → task object)
 * @param {Array} params.allTasks - All available tasks formatted for dropdown
 * @param {Array} params.originalSectionData - Original section data with attribution flags
 * @returns {Object} Task selection state and handlers
 * 
 * @example
 * const {
 *   selectedTasks,
 *   selectedItems,
 *   handleTaskChange,
 *   handleAttributionChange
 * } = useWorkOrderTaskSelection({ taskByIdRef, allTasks, originalSectionData });
 */
export const useWorkOrderTaskSelection = ({
  taskByIdRef,
  allTasks = [],
  originalSectionData = []
}) => {
  const dispatch = useDispatch();
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  /**
   * Finds the parent section with is_attribution_level flag from stage path.
   * Traverses the full hierarchy to find the innermost section with attribution level.
   * 
   * @param {string} stagePath - Full stage path (e.g., "Section Name > SubSection Name")
   * @returns {Object|null} Parent section with is_attribution_level: true, or null
   */
  const findParentSection = (stagePath) => {
    const pathParts = stagePath.split(' > ');

    const findSectionInData = (sections, targetName) => {
      for (const section of sections) {
        if (section.name === targetName) {
          return section;
        }
        if (section.stages && section.stages.length > 0) {
          const found = findSectionInData(section.stages, targetName);
          if (found) return found;
        }
      }
      return null;
    };

    let attributionSection = null;
    let currentSections = originalSectionData;

    for (const sectionName of pathParts) {
      const section = findSectionInData(currentSections, sectionName);

      if (section) {
        if (section.is_attribution_level) {
          attributionSection = section;
        }
        currentSections = section.stages || [];
      }
    }

    return attributionSection;
  };

  /**
   * Handles task selection changes with subcontractor item expansion.
   * Expands tasks to subtasks → subcontractor items for granular line-level display.
   * Handles "Select All" functionality and attribution logic.
   * 
   * @param {Array} selectedOptions - Array of selected task options
   */
  const handleTaskChange = (selectedOptions) => {
    // Handle "Select All" logic first
    if (selectedOptions.some(opt => opt.value === 'select_all')) {
      const allTasksFiltered = allTasks.filter(task => task.value !== 'select_all');
      handleTaskChange(allTasksFiltered);
      return;
    }

    const newSelectedTasks = [];
    const newSelectedItems = [];

    selectedOptions.forEach(opt => {
      const matchedTask = allTasks.find(t => t.value === opt.value);

      // Find existing task by matching taskId
      const existingTask = selectedTasks.find(t => t.taskId === opt.value);

      // If this exact task already exists, preserve it and re-process its items
      if (existingTask) {
        newSelectedTasks.push(existingTask);

        // Re-process subcontractor items for this task
        const task = taskByIdRef.current.get(existingTask.taskId);

        if (task) {
          const subTasks = task.subTasks || [];
          subTasks.forEach(subTask => {
            const subcontractorItems = subTask.subcontractor || [];
            const itemsArray = Array.isArray(subcontractorItems) ? subcontractorItems : [subcontractorItems];

            itemsArray.forEach(item => {
              if (!item) return;

              const normalizedItem = normaliseSubcontractorItem(item);
              const description = buildDescription(
                opt.label,
                subTask.description || '',
                normalizedItem.description
              );

              const existingLineItem = selectedItems.find(existing =>
                existing.subTaskId === subTask.id &&
                existing.subcontractorItemId === normalizedItem.id
              );

              newSelectedItems.push({
                taskId: existingLineItem?.taskId ?? (existingTask.stageId || existingTask.taskId),
                taskName: existingLineItem?.taskName ?? (existingTask.stageName || existingTask.taskName),
                taskType: existingLineItem?.taskType ?? existingTask.taskType,
                // Preserve original task info for proper grouping in ManageItemsModal
                // Use opt.value/label which represent the selected task (WATER, FIRE INTERNAL)
                originalTaskId: existingLineItem?.originalTaskId ?? opt.value,
                originalTaskName: existingLineItem?.originalTaskName ?? opt.label,
                // Preserve full stage path for hierarchical display
                stagePath: existingLineItem?.stagePath ?? existingTask.stagePath ?? opt.stagePath,
                subTaskId: subTask.id,
                subTaskName: subTask.description || '',
                subcontractorItemId: normalizedItem.id,
                description: existingLineItem?.description ?? description,
                cost: normalizedItem.Cost,
                amount: existingLineItem?.amount ?? normalizedItem.saleAmount
              });
            });
          });
        }
        return;
      }

      // New task - check if it's from an attribution-level section
      let taskData = {
        taskId: opt.value,
        taskName: opt.label,
        cost: matchedTask?.subcontractorTotal_Cost || 0,
        amount: matchedTask?.subcontractorTotal_Cost || 0,
        taskType: TaskType.TASK
      };

      // If this is a task (not a section) and has stagePath, check if it's from an attribution-level section
      if (opt.taskType && opt.stagePath) {
        const parentSection = findParentSection(opt.stagePath);

        // If parent section has is_attribution_level, use stage attribution
        if (parentSection && parentSection.is_attribution_level) {
          taskData = {
            stageId: parentSection.id,
            taskId: opt.value,
            taskName: `${opt.label} (${opt.stagePath})`,
            stageName: parentSection.name,
            stagePath: opt.stagePath, // Preserve full path for modal display
            cost: matchedTask?.subcontractorTotal_Cost || 0,
            amount: matchedTask?.subcontractorTotal_Cost || 0,
            taskType: TaskType.STAGE
          };
        } else if (opt.stagePath) {
          // Task has a parent but parent is not attribution level
          // Preserve path for display purposes
          taskData.stagePath = opt.stagePath;
        }
      } else if (opt.stagePath) {
        // Section or task with path but no special attribution
        taskData.stagePath = opt.stagePath;
      }

      // Expand task to subcontractor line items
      const task = taskByIdRef.current.get(taskData.taskId);
      const itemsCountBefore = newSelectedItems.length;

      if (task) {
        const subTasks = task.subTasks || [];
        subTasks.forEach(subTask => {
          const subcontractorItems = subTask.subcontractor || [];
          const itemsArray = Array.isArray(subcontractorItems) ? subcontractorItems : [subcontractorItems];

          itemsArray.forEach(item => {
            if (!item) return;

            const normalizedItem = normaliseSubcontractorItem(item);
            const description = buildDescription(
              opt.label,
              subTask.description || '',
              normalizedItem.description
            );

            const existingLineItem = selectedItems.find(existing =>
              existing.subTaskId === subTask.id &&
              existing.subcontractorItemId === normalizedItem.id
            );

            newSelectedItems.push({
              taskId: existingLineItem?.taskId ?? (taskData.stageId || taskData.taskId),
              taskName: existingLineItem?.taskName ?? (taskData.stageName || taskData.taskName),
              taskType: existingLineItem?.taskType ?? taskData.taskType,
              // Preserve original task info for proper grouping in ManageItemsModal
              // Use opt.value/label which represent the selected task (WATER, FIRE INTERNAL)
              originalTaskId: existingLineItem?.originalTaskId ?? opt.value,
              originalTaskName: existingLineItem?.originalTaskName ?? opt.label,
              // Preserve full stage path for hierarchical display
              stagePath: existingLineItem?.stagePath ?? taskData.stagePath ?? opt.stagePath,
              subTaskId: subTask.id,
              subTaskName: subTask.description || '',
              subcontractorItemId: normalizedItem.id,
              description: existingLineItem?.description ?? description,
              cost: normalizedItem.Cost,
              amount: existingLineItem?.amount ?? normalizedItem.saleAmount
            });
          });
        });
      }

      // Only show task in dropdown if it has at least one subcontractor item
      if (newSelectedItems.length > itemsCountBefore) {
        newSelectedTasks.push(taskData);
      }
    });

    // Preserve manual items by appending them after task-sourced items
    const manualItems = selectedItems.filter(it => it.isManual);
    const finalSelectedItems = [...newSelectedItems, ...manualItems];

    setSelectedTasks(newSelectedTasks);
    setSelectedItems(finalSelectedItems);
  };

  /**
   * Handles attribution dropdown change for a specific line item.
   * Updates the taskId, taskName, and taskType for the selected row.
   * 
   * @param {number} index - Index of the item in selectedItems array
   * @param {object} selectedOption - Selected option object from TaskMultiSelect
   */
  const handleAttributionChange = (index, selectedOption) => {
    if (!selectedOption) return;

    setSelectedItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        taskId: selectedOption.value,
        taskName: selectedOption.label,
        taskType: selectedOption.taskType || (selectedOption.isStage ? TaskType.STAGE : TaskType.TASK),
        stagePath: selectedOption.stagePath
      };
      return updated;
    });
  };

  /**
   * Updates a line item's description.
   * Custom descriptions persist across MultiSelect changes and attribution changes.
   * 
   * @param {number} index - Index of item in selectedItems array
   * @param {string} value - New description text
   */
  const handleDescriptionChange = (index, value) => {
    setSelectedItems(prev => {
      const updated = [...prev];
      updated[index].description = value;
      return updated;
    });
  };

  /**
   * Handles cost or amount changes for a line item.
   * Validates that values are non-negative.
   * 
   * @param {number} index - Index of item in selectedItems array
   * @param {string} key - Field key ('cost' or 'amount')
   * @param {string|number} value - New value
   */
  const handleCostChange = (index, key, value) => {
    setSelectedItems(prev => {
      const updated = [...prev];
      const parsedValue = parseFloat(value);
      updated[index][key] = parsedValue < 0 ? 0 : parsedValue;
      return updated;
    });
  };

  /**
   * Removes a line item from the table.
   * Does not affect selectedTasks or MultiSelect - only removes from selectedItems.
   * 
   * @param {number} index - Index of item to remove from selectedItems array
   */
  const handleRemoveLineItem = (index) => {
    setSelectedItems(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  /**
   * Adds a manual line item to the table.
   * Manual items have editable description, amount, and task/section dropdown.
   * Contractor cost defaults to 0.
   * User must select a task/section before submit.
   */
  const handleAddManualItem = () => {
    const newManualItem = {
      isManual: true,
      manualId: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      id: `wo-item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, // Unique line item ID for invoice matching
      taskId: null,
      taskName: null,
      taskType: null,
      subTaskId: null,
      subTaskName: '',
      subcontractorItemId: null,
      description: '',
      amount: 0,
      cost: 0
    };

    setSelectedItems(prev => [...prev, newManualItem]);
  };

  /**
   * Validates that all line items have a task/section selected.
   * Used before form submission.
   * 
   * @returns {boolean} True if all items have taskId, false otherwise
   */
  const validateLineItems = () => {
    const itemsMissingTask = selectedItems.filter(it => it.taskId == null || it.taskId === '');

    if (itemsMissingTask.length > 0) {
      return false;
    }

    return true;
  };

  return {
    selectedTasks,
    selectedItems,
    handleTaskChange,
    handleAttributionChange,
    handleDescriptionChange,
    handleCostChange,
    handleRemoveLineItem,
    handleAddManualItem,
    validateLineItems,
    setSelectedTasks,
    setSelectedItems,
  };
};
